import { useMemo } from "react";
import { useIntercom } from "react-use-intercom";
import { Tick, Card, CircleExclamationOutlined } from "../../Assets/Icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeals } from "../../CustomHooks/Queries";
import {
  formatDate,
  getBankAccountMigrationEndDateFromDealsResponse,
  getBankAccountMigrationStatusFromDealsResponse,
  getBankMigrationSkippedStatusKey,
  shouldShowBankAccountMigrationSkipFromDealsResponse,
  LocalStorage,
} from "../../Utils/helpers";

import { longMonthDateFormat } from "../../Utils/constants";
import "./bank-migration.scss";

const STAYS_THE_SAME_ITEMS = [
  {
    title: "Account Balance & Transfers",
    description: "Your balance, pending deposits, and transfers carry over automatically.",
  },
  {
    title: "Connected Accounts",
    description: "Linked bank accounts and cards stay connected. Nothing to do.",
  },
  {
    title: "Recurring & Scheduled Transfers",
    description: "Recurring deposits and scheduled transfers continue as-is.",
  },
  {
    title: "Activity & Statements",
    description: "Your full transaction history comes with you.",
  },
];

const WHATS_CHANGING_ITEMS = [
  {
    Icon: Card,
    title: "Stake Visa Debit Cards",
    description: "You'll receive a new debit card. Your current card will be deactivated.",
  },
  {
    Icon: CircleExclamationOutlined,
    title: "Express Paycheck",
    description:
      "Express Paycheck will pause during the transition. You can re-enroll once your new account is active.",
  },
];

function MigrationListItem({ Icon, iconVariant, title, description }) {
  return (
    <li className={`migration-item ${iconVariant}`}>
      <span className="item-icon">
        <Icon />
      </span>
      <div className="item-content">
        <h3 className="item-title">{title}</h3>
        <p className="item-description">{description}</p>
      </div>
    </li>
  );
}

export default function BankMigration({ migrationDeadline, onAccept, onSkip }) {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { show: showIntercom } = useIntercom();
  const { data: dealsData } = useDeals();
  const showSkipButton = shouldShowBankAccountMigrationSkipFromDealsResponse(dealsData);
  const migrationDeadlineText = useMemo(() => {
    if (migrationDeadline) {
      return migrationDeadline;
    }

    return formatDate(getBankAccountMigrationEndDateFromDealsResponse(dealsData), longMonthDateFormat);
  }, [dealsData, migrationDeadline]);

  const handleAccept = () => {
    if (onAccept) {
      onAccept();
      return;
    }

    navigate("/bank-migration-details");
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
      return;
    }

    const migrationStatus =
      locationState?.migrationStatus || getBankAccountMigrationStatusFromDealsResponse(dealsData);
    if (migrationStatus) {
      LocalStorage.write(getBankMigrationSkippedStatusKey(), migrationStatus);
    }
    navigate("/home", { replace: true });
  };

  return (
    <section className="authenticated-page bank-migration">
      <div className="migration-card">
        <h1 className="card-title">Required: Account Migration</h1>
        <div className="card-description">
          <p>
            Your money and account details are safe. To continue using Stake, you&apos;ll need to accept a one-time
            account migration.
          </p>
          {migrationDeadlineText ? (
            <p>Complete this migration by {migrationDeadlineText} to keep your account active.</p>
          ) : null}
        </div>
        <button type="button" className="faq-link" onClick={() => showIntercom()}>
          Contact support for more info
        </button>

        <div className="migration-section stays-same">
          <h2 className="section-label">What stays the same</h2>
          <ul className="migration-list">
            {STAYS_THE_SAME_ITEMS.map((item) => (
              <MigrationListItem key={item.title} Icon={Tick} iconVariant="check" {...item} />
            ))}
          </ul>
        </div>

        <div className="migration-section whats-changing">
          <h2 className="section-label">What&apos;s changing</h2>
          <ul className="migration-list">
            {WHATS_CHANGING_ITEMS.map((item) => (
              <MigrationListItem key={item.title} iconVariant="warning" {...item} />
            ))}
          </ul>
        </div>

        <div className="migration-actions">
          <button type="button" className="accept-button" onClick={handleAccept}>
            Accept &amp; continue
          </button>
          {showSkipButton ? (
            <button type="button" className="skip-button" onClick={handleSkip}>
              Skip for now
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
